import React, { FC, useState } from 'react';
import { alpha, styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { BODY_FONT, g, t, TICKET } from '../../HomeScreen/HomeScreen.styles';

const BlockTitle = styled('h2')({
  fontSize: t(27),
  lineHeight: 1.2,
  letterSpacing: '-0.015em',
  fontWeight: 700,
  fontFamily: BODY_FONT,
  color: TICKET.labelInk,
  margin: 0,
  textAlign: 'center',
});

const SubHint = styled(Box)({
  textAlign: 'center',
  color: '#6d7175',
  fontWeight: 500,
  fontFamily: BODY_FONT,
  letterSpacing: '.22em',
  textIndent: '.22em',
  marginTop: g(12),
  fontSize: t(14),
});

const ButtonRow = styled(Box)({
  margin: `${g(20)} auto 0`,
  maxWidth: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  gap: g(6),
});

const AnswerButton = styled('button', { shouldForwardProp: (p) => p !== '$selected' })<{ $selected?: boolean }>(
  ({ theme, $selected }) => ({
    width: `max(48px, ${g(92)})`,
    height: `max(48px, ${g(92)})`,
    borderRadius: '50%',
    border: 'none',
    background: $selected ? alpha(theme.palette.primary.main, 0.14) : 'transparent',
    display: 'grid',
    placeItems: 'center',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    transition: 'transform 220ms ease, background 180ms ease',
    lineHeight: 1,
    userSelect: 'none',
    transform: $selected ? 'scale(1.08)' : 'scale(1)',
    '& span': { fontSize: `max(30px, ${g(58)})` },
    '&:hover': !$selected ? { background: 'rgba(0,0,0,.04)', transform: 'scale(1.04)' } : {},
    '&:active': !$selected ? { transform: 'scale(1.02)', background: 'rgba(0,0,0,.06)' } : {},
    '&:focus-visible': { outline: `3px solid ${theme.palette.primary.main}`, outlineOffset: '2px' },
    '&:disabled': { opacity: 0.55, cursor: 'default', transform: 'none' },
  }),
);

interface Props {
  question: string;
  onAnswer: (answer: string) => void;
}

const ANSWERS = [
  { label: '\u{1F620}', value: '1' },
  { label: '\u{1F61E}', value: '2' },
  { label: '\u{1F610}', value: '3' },
  { label: '\u{1F601}', value: '4' },
  { label: '\u{1F929}', value: '5' },
];

export const QuestionBlock: FC<Props> = ({ question, onAnswer }) => {
  const [selected, setSelected] = useState<string | null>(null);
  const [disabled, setDisabled] = useState(false);

  const handleClick = (value: string) => {
    if (disabled) return;
    setSelected(value);
    setDisabled(true);
    // Auto-advance after brief delay
    setTimeout(() => onAnswer(value), 400);
  };

  return (
    <section aria-label={question}>
      <BlockTitle>{question}</BlockTitle>
      <SubHint>TAP TO SELECT</SubHint>
      <ButtonRow role="group" aria-label={question}>
        {ANSWERS.map(({ label, value }) => (
          <AnswerButton
            key={value}
            $selected={selected === value}
            disabled={disabled && selected !== value}
            onClick={() => handleClick(value)}
            aria-label={value}
          >
            <span>{label}</span>
          </AnswerButton>
        ))}
      </ButtonRow>
    </section>
  );
};
